// Copyright Caproc Oy
// System name: Caproc IoT
// System module: Sensor
// Author: TS
//
// 16.4.2026 TS (claude) Initial version - AI Chat for sensor and sensor group analysis
// 16.4.2026 TS (claude) Refactored to async Celery task model with polling

import React from "react";
import { Message, Header, Button, Segment, Dimmer, Loader, Image, Select, TextArea, Form, Progress } from "semantic-ui-react";
import ReactMarkdown from "react-markdown";
import Shell from "./Shell";
import { authAxios } from "../../utils";
import { setActivity } from "../../store/actions/auth";
import { withRouter, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import { withTranslation } from 'react-i18next';
import { sensorDetailsURL, aiChatURL, aiChatResultURL, aiUsageURL } from "../../constants";


class SensorAIChat extends React.Component {
    state = {
        sensor: null,
        error: null,
        loading: false,
        sending: false,
        question: "",
        time_range: "7d",
        answer: null,
        tokens_used: null,
        tokens_remaining: null,
        monthly_tokens_used: 0,
        monthly_token_limit: 0,
    };

    pollTimer = null;

    componentDidMount() {
        this.props.setActivity();
        this.handleFetchSensor();
        this.handleFetchUsage();
    }

    componentWillUnmount() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
        }
    }

    handleFetchSensor = () => {
        this.setState({ loading: true });
        const { activeCompanyId } = this.props;
        const { sensor_id } = this.props.match.params;
        authAxios
            .get(sensorDetailsURL(activeCompanyId, sensor_id))
            .then(res => {
                this.setState({
                    sensor: res.data,
                    loading: false,
                    error: null
                });
            })
            .catch(err => {
                this.setState({ loading: false, error: err });
            });
    };

    handleFetchUsage = () => {
        const { activeCompanyId } = this.props;
        authAxios
            .get(aiUsageURL(activeCompanyId))
            .then(res => {
                this.setState({
                    monthly_tokens_used: res.data.monthly_tokens_used,
                    monthly_token_limit: res.data.monthly_token_limit
                });
            })
            .catch(err => {
                // AI not enabled - silently ignore
            });
    };

    handleSend = () => {
        const { question, time_range } = this.state;
        if (!question.trim()) return;

        this.setState({ sending: true, error: null, answer: null });
        const { activeCompanyId } = this.props;
        const { sensor_id } = this.props.match.params;

        authAxios
            .post(aiChatURL(activeCompanyId), {
                question: question,
                sensor_id: sensor_id,
                time_range: time_range
            })
            .then(res => {
                const taskId = res.data.task_id;
                this.startPolling(taskId);
            })
            .catch(err => {
                const errorMsg = err.response && err.response.data && err.response.data.error
                    ? err.response.data.error
                    : err.response && err.response.data && err.response.data.detail
                        ? err.response.data.detail
                        : "AI service error";
                this.setState({
                    sending: false,
                    error: errorMsg
                });
            });
    };

    startPolling = (taskId) => {
        const { activeCompanyId } = this.props;
        let pollCount = 0;
        const maxPolls = 60; // Max 2 minutes (60 x 2s)

        this.pollTimer = setInterval(() => {
            pollCount++;
            if (pollCount > maxPolls) {
                clearInterval(this.pollTimer);
                this.setState({
                    sending: false,
                    error: "Analysis timed out. Please try again."
                });
                return;
            }

            authAxios
                .get(aiChatResultURL(activeCompanyId, taskId))
                .then(res => {
                    if (res.data.status === 'completed') {
                        clearInterval(this.pollTimer);
                        this.setState({
                            answer: res.data.answer,
                            tokens_used: res.data.tokens_used,
                            tokens_remaining: res.data.tokens_remaining,
                            sending: false,
                            error: null
                        });
                        this.handleFetchUsage();
                    } else if (res.data.status === 'failed') {
                        clearInterval(this.pollTimer);
                        this.setState({
                            sending: false,
                            error: res.data.error || "AI analysis failed."
                        });
                    }
                    // pending/processing - continue polling
                })
                .catch(err => {
                    clearInterval(this.pollTimer);
                    this.setState({
                        sending: false,
                        error: "Failed to fetch result."
                    });
                });
        }, 2000);
    };

    handleQuestionChange = (e) => {
        this.setState({ question: e.target.value });
    };

    handleTimeRangeChange = (e, { value }) => {
        this.setState({ time_range: value });
    };

    handleSuggestionClick = (suggestion) => {
        this.setState({ question: suggestion }, () => {
            this.handleSend();
        });
    };

    handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.handleSend();
        }
    };

    render() {
        const { sensor, error, loading, sending, question, time_range, answer, monthly_tokens_used, monthly_token_limit } = this.state;
        const { t, isAuthenticated } = this.props;

        if (!isAuthenticated) {
            return <Redirect to="/login" />;
        }

        const timeRangeOptions = [
            { key: '24h', value: '24h', text: t("Last 24 hours") },
            { key: '7d', value: '7d', text: t("Last 7 days") },
            { key: '30d', value: '30d', text: t("Last 30 days") },
            { key: '90d', value: '90d', text: t("Last 90 days") },
        ];

        const suggestions = [
            t("Summarize the current status"),
            t("Are there any anomalies?"),
            t("Is the consumption normal?"),
            t("Is the concrete ready for coating?"),
        ];

        const usagePercent = monthly_token_limit > 0
            ? Math.min(100, Math.round((monthly_tokens_used / monthly_token_limit) * 100))
            : 0;

        return (
            <Shell
                active_sensor_id={sensor && (sensor.id)}
                active_sensor_name={sensor && (sensor.local_name)}
                active_sensor_device_name={sensor && (sensor.device_name)}
                active_sensor_model={sensor && (sensor.model_name)}
                active_sensor_type={sensor && sensor.sensor_model && (sensor.sensor_model.sensor_type)}
            >
                {loading ? (
                    <Segment>
                        <Dimmer active inverted>
                            <Loader inverted>Loading</Loader>
                        </Dimmer>
                        <Image src="https://react.semantic-ui.com/images/wireframe/short-paragraph.png" />
                    </Segment>
                ) : (
                    <>
                        <Header as="h2">{t("AI Analysis")}</Header>
                        {sensor && (
                            <p>{sensor.device_name} - {sensor.local_name || sensor.device_address}</p>
                        )}

                        {/* Usage meter */}
                        {monthly_token_limit > 0 && (
                            <Segment>
                                <p>{t("Token usage")}: {monthly_tokens_used.toLocaleString('fi-FI')} {t("of")} {monthly_token_limit.toLocaleString('fi-FI')}</p>
                                <Progress
                                    percent={usagePercent}
                                    color={usagePercent > 90 ? 'red' : usagePercent > 70 ? 'yellow' : 'green'}
                                    size="small"
                                />
                                {usagePercent >= 100 && (
                                    <Message warning>{t("Monthly usage limit reached")}</Message>
                                )}
                            </Segment>
                        )}

                        {/* Question input */}
                        <Segment>
                            <Form>
                                <Form.Field>
                                    <label>{t("Time range")}</label>
                                    <Select
                                        options={timeRangeOptions}
                                        value={time_range}
                                        onChange={this.handleTimeRangeChange}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <TextArea
                                        placeholder={t("Ask a question about sensor data...")}
                                        value={question}
                                        onChange={this.handleQuestionChange}
                                        onKeyDown={this.handleKeyDown}
                                        rows={3}
                                    />
                                </Form.Field>
                                <Button
                                    primary
                                    onClick={this.handleSend}
                                    loading={sending}
                                    disabled={sending || !question.trim() || usagePercent >= 100}
                                >
                                    {t("Send")}
                                </Button>
                            </Form>
                        </Segment>

                        {/* Suggestions */}
                        {!answer && !sending && (
                            <Segment>
                                <Header as="h4">{t("Question suggestions")}</Header>
                                {suggestions.map((s, i) => (
                                    <Button
                                        key={i}
                                        basic
                                        size="small"
                                        style={{ margin: '2px' }}
                                        onClick={() => this.handleSuggestionClick(s)}
                                    >
                                        {s}
                                    </Button>
                                ))}
                            </Segment>
                        )}

                        {/* Loading indicator */}
                        {sending && (
                            <Segment>
                                <Dimmer active inverted>
                                    <Loader>{t("Analyzing...")}</Loader>
                                </Dimmer>
                                <Image src="https://react.semantic-ui.com/images/wireframe/paragraph.png" />
                            </Segment>
                        )}

                        {/* Answer */}
                        {answer && (
                            <Segment>
                                <div className="ai-answer" style={{ lineHeight: '1.6' }}>
                                    <ReactMarkdown>{answer}</ReactMarkdown>
                                </div>
                            </Segment>
                        )}

                        {/* Error */}
                        {error && (
                            <Message negative>
                                <Message.Header>{t("Error")}</Message.Header>
                                <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
                            </Message>
                        )}
                    </>
                )}
            </Shell>
        );
    }
}

const mapStateToProps = state => {
    return {
        activeCompanyId: state.company.activeCompanyId,
        isAuthenticated: state.auth.token !== null
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setActivity: () => dispatch(setActivity())
    };
};

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(withTranslation('sensoraichat')(SensorAIChat))
);
